import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { formatCurrency } from './currency';
import { formatCustomerAccountAmount } from './customerAccountAmount';

export interface DebtSummaryMovement {
  description?: string;
  summaryText?: string;
  date: string;
  sortDate?: string;
  amount: number;
}

export interface CustomerDebtSummaryLabels {
  summaryTitle: string;
  customerLabel: string;
  balanceLabel: string;
  dateLabel: string;
  recentMovementsLabel: string;
  noRecentMovements: string;
  debtLabel: string;
  paymentLabel: string;
  customerOwes: string;
  youOwe: string;
  noBalance: string;
  noDescription: string;
}

export interface CustomerDebtSummaryInput {
  customerName: string;
  totalDebt: number;
  generatedAt: string;
  period?: string;
  periodLabel?: string;
  recentMovements: DebtSummaryMovement[];
  labels: CustomerDebtSummaryLabels;
}

export interface CustomerDebtSummary {
  customerName: string;
  totalDebt: number;
  balanceLine: string;
  message: string;
  fileName: string;
}

export const normalizeDebtBalance = (totalDebt: number): number => (
  Number.isFinite(totalDebt) ? totalDebt : 0
);

export const formatDebtSummaryBalance = (totalDebt: number, labels: CustomerDebtSummaryLabels): string => {
  const safeBalance = normalizeDebtBalance(totalDebt);
  if (safeBalance > 0) return `${labels.customerOwes}: ${formatCurrency(safeBalance)}`;
  if (safeBalance < 0) return `${labels.youOwe}: ${formatCurrency(Math.abs(safeBalance))}`;
  return labels.noBalance;
};

const movementLine = (movement: DebtSummaryMovement, labels: CustomerDebtSummaryLabels): string | null => {
  if (!Number.isFinite(movement.amount)) return null;
  const description = movement.summaryText?.trim() || movement.description?.trim() || labels.noDescription;
  return `• ${movement.date} — ${description}: ${formatCustomerAccountAmount(movement.amount)}`;
};

const recentMovementLines = (input: CustomerDebtSummaryInput): string[] => input.recentMovements
  .map(movement => movementLine(movement, input.labels))
  .filter((line): line is string => Boolean(line));

export const createCustomerDebtSummary = (input: CustomerDebtSummaryInput): CustomerDebtSummary => {
  const customerName = input.customerName.trim() || input.labels.customerLabel;
  const totalDebt = normalizeDebtBalance(input.totalDebt);
  const balanceLine = formatDebtSummaryBalance(totalDebt, input.labels);
  const movementLines = recentMovementLines(input);
  const messageLines = [
    input.labels.summaryTitle,
    `${input.labels.customerLabel}: ${customerName}`,
    `${input.labels.balanceLabel}: ${balanceLine}`,
    `${input.labels.dateLabel}: ${input.generatedAt}`,
  ];
  if (input.period && input.periodLabel) messageLines.push(`${input.periodLabel}: ${input.period}`);

  if (movementLines.length > 0) {
    messageLines.push('', input.labels.recentMovementsLabel, ...movementLines);
  } else {
    messageLines.push('', input.labels.recentMovementsLabel, input.labels.noRecentMovements);
  }

  const slug = customerName
    .toLocaleLowerCase('tr-TR')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'musteri';

  return {
    customerName,
    totalDebt,
    balanceLine,
    message: messageLines.join('\n'),
    fileName: `veresiye-borc-ozeti-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`,
  };
};

export const createCustomerDebtSummaryDocument = (input: CustomerDebtSummaryInput): TDocumentDefinitions => {
  const summary = createCustomerDebtSummary(input);
  const movementLines = recentMovementLines(input);

  return {
    pageSize: 'A4',
    pageMargins: [40, 44, 40, 44],
    defaultStyle: {
      font: 'Roboto',
      fontSize: 10,
      color: '#24312b',
    },
    styles: {
      title: {
        fontSize: 20,
        bold: true,
        color: '#236044',
        margin: [0, 0, 0, 8],
      },
      customerName: {
        fontSize: 15,
        bold: true,
        margin: [0, 0, 0, 18],
      },
      label: {
        fontSize: 9,
        bold: true,
        color: '#66736d',
        margin: [0, 14, 0, 4],
      },
      balance: {
        fontSize: 14,
        bold: true,
        color: summary.totalDebt > 0 ? '#a84c40' : summary.totalDebt < 0 ? '#3f7047' : '#66736d',
        margin: [0, 0, 0, 4],
      },
      movement: {
        fontSize: 9,
        margin: [0, 0, 0, 5],
      },
      footer: {
        fontSize: 8,
        color: '#66736d',
      },
    },
    content: [
      { text: input.labels.summaryTitle, style: 'title' },
      { text: summary.customerName, style: 'customerName' },
      { text: input.labels.balanceLabel, style: 'label' },
      { text: summary.balanceLine, style: 'balance' },
      { text: `${input.labels.dateLabel}: ${input.generatedAt}`, style: 'footer', margin: [0, 0, 0, 14] },
      ...(input.period && input.periodLabel
        ? [{ text: `${input.periodLabel}: ${input.period}`, style: 'footer' as const, margin: [0, 0, 0, 14] }]
        : []),
      { text: input.labels.recentMovementsLabel, style: 'label' },
      ...(movementLines.length > 0
        ? movementLines.map(text => ({ text, style: 'movement' as const }))
        : [{ text: input.labels.noRecentMovements, style: 'movement' as const }]),
    ] as TDocumentDefinitions['content'],
  };
};
